import type { QualityScoreLevel } from '@/constants';
import { QUALITY_SCORE_COLORS, QUALITY_SCORE_LABELS, QUALITY_SCORE_LEVELS, QUALITY_SCORE_THRESHOLDS } from '@/constants';

import type { CallLogsMeta } from '@/types/calls';

/**
 * Parses JSON metadata from call summary.
 *
 * @param {CallLogsMeta[]} metadata - Array of call metadata objects
 * @returns {Object} Parsed metadata object or default value
 */
export const parseCallSummary = (metadata: CallLogsMeta[] | undefined) => {
    const summary = metadata?.find(meta => meta.name.includes('sttMonoSummary'));

    if (!summary) {
        return null;
    }

    try {
        const parsedSummary = JSON.parse(summary?.value);

        return { created_at: summary?.created_at, summary: parsedSummary };
    } catch {
        return null;
    }
};

/**
 * Returns the color for a quality score based on percentage and shade.
 *
 * @param {number} percentage - Quality score as a percentage of the maximum score
 * @param {'light' | 'dark'} shade - Which variant to return (e.g. light for backgrounds, dark for accent)
 * @returns {string} Color theme path (e.g. "green.30")
 */
export const getQualityScoreColor = (percentage: number, shade: 'light' | 'dark'): string => {
    const level = getQualityScoreLevel(percentage);

    return QUALITY_SCORE_COLORS[level][shade];
};

/**
 * Returns the quality score level (good, neutral, needs_attention).
 *
 * @param {number} percentage - Quality score as a percentage of the maximum score
 * @returns {string} Quality level key
 */
export const getQualityScoreLevel = (percentage: number): QualityScoreLevel => {
    switch (true) {
        case percentage >= QUALITY_SCORE_THRESHOLDS.GOOD_MIN:
            return QUALITY_SCORE_LEVELS.GOOD;
        case percentage >= QUALITY_SCORE_THRESHOLDS.NEUTRAL_MIN:
            return QUALITY_SCORE_LEVELS.NEUTRAL;
        default:
            return QUALITY_SCORE_LEVELS.NEEDS_ATTENTION;
    }
};

/**
 * Returns the quality score description (good, neutral, needs attention).
 *
 * @param {number} percentage - Quality score as a percentage of the maximum score
 * @returns {string} Human-readable quality level
 */
export const getQualityScoreDescription = (percentage: number): string => {
    switch (true) {
        case percentage >= QUALITY_SCORE_THRESHOLDS.GOOD_MIN:
            return QUALITY_SCORE_LABELS[QUALITY_SCORE_LEVELS.GOOD];
        case percentage >= QUALITY_SCORE_THRESHOLDS.NEUTRAL_MIN:
            return QUALITY_SCORE_LABELS[QUALITY_SCORE_LEVELS.NEUTRAL];
        default:
            return QUALITY_SCORE_LABELS[QUALITY_SCORE_LEVELS.NEEDS_ATTENTION];
    }
};

/**
 * Extracts quality score from call metadata for a specific formula.
 *
 * @param {CallLogsMeta[] | undefined} metadata - Array of call metadata objects
 * @param {string | null | undefined} formulaId - Quality score formula ID to look for
 * @returns {number | null} Quality score value or null if not found
 */
export const extractQualityScore = (metadata: CallLogsMeta[] | undefined, formulaId: string | null | undefined): number | null => {
    if (!metadata || !formulaId) return null;

    const scoreEntry = metadata.find(meta => meta.leadapp === 'AiInsights' && meta.name === formulaId);

    if (!scoreEntry) return null;

    const score = parseFloat(scoreEntry.value);

    return isNaN(score) ? null : score;
};

/**
 * Returns a list of unique, non-empty call end reasons.
 *
 * Filters out falsy values such as `undefined`, `null`, and empty strings,
 * then removes duplicates while preserving the first-seen order.
 *
 * @param {Array<{ reason_name?: string | null }>} calls - List of call objects
 * @returns {string[]} Unique valid call end reasons
 */
export const getUniqueCallEndReasons = (calls: Array<{ reason_name?: string | null }>) => {
    return [...new Set(calls.map(call => call.reason_name).filter((reason): reason is string => Boolean(reason)))];
};
